import React, { useEffect, useMemo, useState } from 'react';
import { Field } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import { PartnerSearchContext } from '@css-xp/ppc-types';
import { searchContextKey } from './search-form';
import './search-form.scss';

interface SearchContextContainerProps {
  defaultValue?: string;
  onChange?: (context: SearchContextSelectItem) => void;
  placeholder?: string;
}

export interface SearchContextSelectItem {
  label: string;
  value: PartnerSearchContext;
  placeHolderText?: string;
}

export const SEARCH_CONTEXT_ALL: SearchContextSelectItem = { label: 'All', value: PartnerSearchContext.ALL };
const SEARCH_CONTEXT_EMAIL = {
  label: 'Email',
  value: PartnerSearchContext.EMAIL,
  placeHolderText: 'Enter email subject or related keywords',
};

const SEARCH_CONTEXT_QUOTES = { label: 'Quote', value: PartnerSearchContext.QUOTE, placeHolderText: 'Enter QT-#' };

const SEARCH_CONTEXT_BILLING_PROFILE = {
  label: 'Billing profile',
  value: PartnerSearchContext.INVOICE_GROUP,
  placeHolderText: 'Enter IG-#',
};

const searchContexts: Array<SearchContextSelectItem> = [
  SEARCH_CONTEXT_ALL,
  { label: 'Subscription', value: PartnerSearchContext.SUBSCRIPTION_ID, placeHolderText: 'Enter E-#' },
  { label: 'Invoice', value: PartnerSearchContext.INVOICE_ID, placeHolderText: 'Enter IN-# or PO Number' },
  { label: 'Refund', value: PartnerSearchContext.REFUND_ID, placeHolderText: 'Enter IN-# or CN-#' },
  { label: 'Company', value: PartnerSearchContext.COMPANY, placeHolderText: 'Enter name' },
  { label: 'Sold-to address', value: PartnerSearchContext.CUSTOMER_ADDRESS, placeHolderText: 'Enter address' },
  { label: 'Site URL', value: PartnerSearchContext.SITE_URL, placeHolderText: 'Enter URL' },
  { label: 'Person', value: PartnerSearchContext.PERSON, placeHolderText: 'Enter Email id' },
];

export const SearchContextsMap: Map<PartnerSearchContext, SearchContextSelectItem> = new Map(
  searchContexts.map((searchContext) => [searchContext.value, searchContext])
);

export const SearchContextContainer = ({ defaultValue, onChange, placeholder }: SearchContextContainerProps) => {
  const defaultOption = useMemo(() => {
    return searchContexts.find((searchContext) => searchContext.value === defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    setSearchContext(defaultOption);
  }, [defaultOption]);

  if (!searchContexts.some((searchContext) => searchContext.value === PartnerSearchContext.QUOTE)) {
    searchContexts.push(SEARCH_CONTEXT_QUOTES);
  }

  if (!searchContexts.some((searchContext) => searchContext.value === PartnerSearchContext.INVOICE_GROUP)) {
    searchContexts.push(SEARCH_CONTEXT_BILLING_PROFILE);
  }

  // This is temporary should be moved to SearchContexts once will enable and later remove feature-flag
  if (!searchContexts.some((searchContext) => searchContext.value === PartnerSearchContext.EMAIL)) {
    searchContexts.push(SEARCH_CONTEXT_EMAIL);
  }

  const [searchContext, setSearchContext] = useState<SearchContextSelectItem | undefined>(defaultOption);
  return (
    <Field<ValueType<SearchContextSelectItem>, HTMLSelectElement>
      name={searchContextKey}
      defaultValue={searchContext}
      isRequired={true}
    >
      {({ fieldProps }) => (
        <div className="search-context-container" data-testid="search-context-container">
          <Select<SearchContextSelectItem>
            {...fieldProps}
            value={searchContext}
            className="search-context"
            classNamePrefix="search-context"
            isSearchable={false}
            placeholder={placeholder}
            options={searchContexts}
            onChange={(context) => {
              if (context) {
                setSearchContext(context);
                fieldProps.onChange(context);
                if (onChange) {
                  onChange(context);
                }
              }
            }}
          />
        </div>
      )}
    </Field>
  );
};
